import { useState } from 'react'

import useAxios, { mutate } from 'hooks/useAxios'
import {
  getTaxIdErrorMessage,
  getTaxIdMetaForCountry,
  TaxIdFieldConfig,
} from 'libs/tax-id-by-country'

const TAX_ID_URL = 'billing/customers/tax-id/'

interface SavedTaxId {
  value: string | null
  type: string | null
}

interface UseTaxIdFieldParams {
  enabled: boolean
  country?: string | null
}

export interface TaxIdField {
  meta: TaxIdFieldConfig | null
  value: string
  error: string | null
  isDirty: boolean
  onChange: (value: string) => void
  save: () => Promise<boolean>
  reset: () => void
}

export function useTaxIdField({ enabled, country = null }: UseTaxIdFieldParams): TaxIdField {
  const meta = enabled ? getTaxIdMetaForCountry(country) : null
  const { data: savedTaxId } = useAxios({ url: TAX_ID_URL, skip: !enabled }) as {
    data?: SavedTaxId | null
  }
  const { fetch: updateTaxId } = useAxios({ url: TAX_ID_URL, method: 'patch', skip: true })
  const [typedValue, setTypedValue] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [valueCountry, setValueCountry] = useState(country)

  if (valueCountry !== country) {
    setValueCountry(country)
    setTypedValue(null)
    setError(null)
  }

  const isSavedTypeMatching = Boolean(meta) && savedTaxId?.type === meta?.type
  const savedValue = isSavedTypeMatching ? savedTaxId?.value ?? '' : ''
  const value = typedValue ?? savedValue

  function onChange(nextValue: string) {
    setTypedValue(nextValue)
    setError(null)
  }

  function reset() {
    setTypedValue(null)
    setError(null)
  }

  function save() {
    const trimmedValue = value.trim()
    if (!meta || !trimmedValue || (isSavedTypeMatching && savedTaxId?.value === trimmedValue)) {
      return Promise.resolve(true)
    }
    return updateTaxId({ data: { value: trimmedValue, type: meta.type } })
      .then((response: { data?: SavedTaxId }) => {
        const storedTaxId = response?.data
        if (storedTaxId && 'value' in storedTaxId) {
          mutate(TAX_ID_URL, storedTaxId, false)
          setTypedValue(null)
        } else {
          mutate(TAX_ID_URL)
        }
        return true
      })
      .catch((saveError: { response?: { status?: number } }) => {
        setError(getTaxIdErrorMessage(saveError, meta))
        return false
      })
  }

  return { meta, value, error, isDirty: value !== savedValue, onChange, save, reset }
}



// WEBPACK FOOTER //
// ./src/hooks/useTaxIdField/index.ts